import React, { useState, useEffect, useCallback } from "react";
import { Head } from "@inertiajs/react";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import {
    Search,
    Plus,
    Database,
    MessageSquare,
    Mail,
    FileText,
    Calendar,
    CreditCard,
    Cloud,
    Link,
    ExternalLink,
    X,
    ChevronLeft,
    ChevronRight,
    Phone,
    Settings,
    Send,
    RotateCcw,
    Info,
    Hash,
    Shield,
    CheckCircle,
    Download,
    RefreshCw,
} from "lucide-react";
import { FilterDropdown } from "@/components/filter-dropdown";
import { useMobile } from "@/hooks/use-mobile";
import { useUsers } from "@/lib/contexts/user-context";
import { router, usePage } from "@inertiajs/react";
import { useToast } from "@/hooks/use-toast";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";
import { IntegrationFormModal } from "@/components/integrations/integration-form-modal";

// Icon mapping for integrations
const getIntegrationIcon = (name) => {
    const icons = {
        "Twilio": <Phone className="h-8 w-8 text-red-600" />,
        "Slack": <MessageSquare className="h-8 w-8 text-purple-600" />,
        "LeadPerfection": <Database className="h-8 w-8 text-purple-600" />,
        "AccuLynx": <Database className="h-8 w-8 text-blue-600" />,
        "Aspire": <Database className="h-8 w-8 text-green-600" />,
    };
    return icons[name] || <Database className="h-8 w-8 text-gray-600" />;
};

export default function IntegrationsPage({ integrations: databaseIntegrations, permissions = {} }) {
    const isMobile = useMobile();

    // Load saved filters from localStorage on component mount
    const loadSavedFilters = () => {
        try {
            const savedFilters = localStorage.getItem('integrations-filters');
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                return {
                    searchQuery: filters.searchQuery || "",
                    categoryFilter: filters.categoryFilter || "all",
                    statusFilter: filters.statusFilter || "all",
                    page: filters.page || 0,
                    itemsPerPage: filters.itemsPerPage || 10,
                };
            }
        } catch (error) {
            console.error('Error loading saved filters:', error);
        }
        
        // Return default values if no saved filters or error
        return {
            searchQuery: "",
            categoryFilter: "all",
            statusFilter: "all",
            page: 0,
            itemsPerPage: 10,
        };
    };

    // Save filters to localStorage
    const saveFilters = (filters) => {
        try {
            localStorage.setItem('integrations-filters', JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    };

    // Load initial filter values
    const initialFilters = loadSavedFilters();

    const [searchQuery, setSearchQuery] = useState(initialFilters.searchQuery);
    const [categoryFilter, setCategoryFilter] = useState(initialFilters.categoryFilter);
    const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState(null);
    const [isConfigSaving, setIsConfigSaving] = useState(false);
    const [configForm, setConfigForm] = useState({});
    const [testPhoneNumber, setTestPhoneNumber] = useState("");
    const [isTestingIntegration, setIsTestingIntegration] = useState(false);
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
    const [integrationToDisconnect, setIntegrationToDisconnect] = useState(null);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isIntegrationFormOpen, setIsIntegrationFormOpen] = useState(false);
    const [integrationFormMode, setIntegrationFormMode] = useState("create");
    const [selectedMappingIntegration, setSelectedMappingIntegration] = useState(null);
    const { toast } = useToast();
    const { currentUser } = useUsers();
    const { props } = usePage();
    
    // Slack-specific state
    const [slackChannels, setSlackChannels] = useState([]);
    const [isLoadingChannels, setIsLoadingChannels] = useState(false);
    const [channelsLoaded, setChannelsLoaded] = useState(false);
    const [testSlackChannel, setTestSlackChannel] = useState("");

    // Add pagination state for the grid view
    const [page, setPage] = useState(initialFilters.page);
    const [itemsPerPage, setItemsPerPage] = useState(initialFilters.itemsPerPage);

    // Save filters whenever they change
    useEffect(() => {
        const filtersToSave = {
            searchQuery,
            categoryFilter,
            statusFilter,
            page,
            itemsPerPage,
        };
        saveFilters(filtersToSave);
    }, [searchQuery, categoryFilter, statusFilter, page, itemsPerPage]);

    // Handle flash messages from backend
    useEffect(() => {
        if (props.flash?.success) {
            toast({
                title: "Success",
                description: props.flash.success,
                variant: "default",
            });
        }
        if (props.flash?.error || (props.errors && Object.keys(props.errors).length > 0)) {
            const errorMessage = props.flash?.error || props.errors?.error || "An error occurred";
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive",
            });
        }
    }, [props.flash, props.errors, toast]);

    // Check if current user is admin
    // const isAdmin = currentUser?.roles?.[0]?.toLowerCase() === "admin"

    // Redirect non-admin users
    // useEffect(() => {
    //   if (currentUser && !isAdmin) {
    //     toast({
    //       title: "Access Denied",
    //       description: "You need admin privileges to access this page.",
    //       variant: "destructive",
    //     })
    //     router.visit("/")
    //   }
    // }, [currentUser, isAdmin, toast])

    // If not admin, don't render the page
    // if (!isAdmin) {
    //   return null
    // }

    // Reset all filters
    const resetFilters = () => {
        setSearchQuery("");
        setCategoryFilter("all");
        setStatusFilter("all");
        setPage(0); // Reset to first page when clearing filters
    };

    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0;
        if (searchQuery) count++;
        if (categoryFilter !== "all") count++;
        if (statusFilter !== "all") count++;
        return count;
    };

    // Convert database integrations to include icons and proper status
    const integrations = (databaseIntegrations || []).map(integration => ({
        ...integration,
        icon: getIntegrationIcon(integration.name),
        status: integration.type === "mapping"
            ? "mapping"
            : integration.is_active ? "connected" : "not-connected",
    }));

    const reloadIntegrations = () => {
        router.reload({ only: ["integrations"] });
    };

    const openCreateIntegrationModal = () => {
        setIntegrationFormMode("create");
        setSelectedMappingIntegration(null);
        setIsIntegrationFormOpen(true);
    };

    const openMappingIntegrationModal = (integration) => {
        setIntegrationFormMode("edit");
        setSelectedMappingIntegration(integration);
        setIsIntegrationFormOpen(true);
    };

    // Filter integrations based on search query and filters
    const filteredIntegrations = integrations.filter((integration) => {
        const matchesSearch =
            integration.name
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
            integration.description
                .toLowerCase()
                .includes(searchQuery.toLowerCase());

        const matchesCategory =
            categoryFilter === "all" || integration.category === categoryFilter;
        const matchesStatus =
            statusFilter === "all" ||
            (statusFilter === "connected" &&
                integration.status === "connected") ||
            (statusFilter === "not-connected" &&
                integration.status === "not-connected");

        return matchesSearch && matchesCategory && matchesStatus;
    });

    // Add this after the filteredIntegrations calculation
    // Paginate the filtered integrations
    const paginatedIntegrations = filteredIntegrations.slice(
        page * itemsPerPage,
        page * itemsPerPage + itemsPerPage
    );

    // Add this function to handle page changes
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handle integration card click
    const handleIntegrationClick = (integration) => {
        if (integration.type === "mapping") {
            openMappingIntegrationModal(integration);
            return;
        }

        // Only allow configuration for Twilio and Slack
        if (integration.name === 'Twilio' || integration.name === 'Slack') {
            openConfigModal(integration);
        } else {
            toast({
                title: "Configuration Not Available",
                description: `Configuration for ${integration.name} is not yet implemented.`,
                variant: "destructive",
            });
        }
    };

    // Open configuration modal
    const openConfigModal = (integration) => {
        setSelectedIntegration(integration);
        setConfigForm(integration.config || {});
        setIsConfigModalOpen(true);
        
        // Load Slack channels if this is a connected Slack integration
        if (integration.name === 'Slack' && integration.is_active) {
            loadSlackChannels(integration);
        }
    };

    // Handle configuration submission
    const handleConfigureIntegration = async (e) => {
        e.preventDefault();
        
        if (!selectedIntegration?.id) {
            toast({
                title: "Error",
                description: "Integration ID is missing. Please try again.",
                variant: "destructive",
            });
            return;
        }

        // Debug: Log the form data being sent
        console.log('Submitting integration config:', {
            integrationId: selectedIntegration.id,
            integrationName: selectedIntegration.name,
            configForm: configForm
        });

        // Validate Slack configuration (either bot token OR OAuth credentials)
        if (selectedIntegration.name === 'Slack') {
            const hasBotToken = configForm.botToken && configForm.botToken.trim();
            const hasOAuthCreds = configForm.clientId && configForm.clientSecret;
            
            // Check if we have either bot token OR OAuth credentials
            if (!hasBotToken && !hasOAuthCreds) {
                toast({
                    title: "Slack Configuration Required",
                    description: "Please provide either Bot Token (for workspace-wide messaging) or OAuth credentials (Client ID + Client Secret for individual user connections)",
                    variant: "destructive",
                });
                return;
            }
            
            // Validate bot token format if provided
            if (hasBotToken && !configForm.botToken.startsWith('xoxb-')) {
                toast({
                    title: "Invalid Bot Token",
                    description: "Bot Token must start with 'xoxb-'",
                    variant: "destructive",
                });
                return;
            }
            
            // Validate OAuth credentials if provided
            if (configForm.clientId && !configForm.clientSecret) {
                toast({
                    title: "Missing Client Secret",
                    description: "Client Secret is required when Client ID is provided",
                    variant: "destructive",
                });
                return;
            }
            
            if (configForm.clientSecret && !configForm.clientId) {
                toast({
                    title: "Missing Client ID", 
                    description: "Client ID is required when Client Secret is provided",
                    variant: "destructive",
                });
                return;
            }
        }
        
        setIsConfigSaving(true);
        
        router.put(`/integrations/${selectedIntegration.id}`, {
            config: configForm
        }, {
            onSuccess: () => {
                // Show success toast first
                toast({
                    title: "Integration Connected",
                    description: `${selectedIntegration.name} has been configured and connected successfully.`,
                });
                
                // Close modal and clear state after a brief delay to ensure toast is visible
                setTimeout(() => {
                    setIsConfigModalOpen(false);
                    setSelectedIntegration(null);
                    setConfigForm({});
                }, 500);
            },
            onError: (errors) => {
                console.error('Configuration error:', errors);
                console.log('Error details:', JSON.stringify(errors, null, 2));
                
                // Extract error message more carefully
                let errorMessage = "Failed to configure integration";
                if (errors.error) {
                    errorMessage = errors.error;
                } else if (errors.message) {
                    errorMessage = errors.message;
                } else if (typeof errors === 'string') {
                    errorMessage = errors;
                } else if (errors.errors) {
                    // Handle Laravel validation errors
                    const validationErrors = Object.values(errors.errors).flat();
                    errorMessage = validationErrors.length > 0 ? validationErrors.join(', ') : errorMessage;
                }
                
                toast({
                    title: "Error",
                    description: errorMessage,
                    variant: "destructive",
                });
            },
            onFinish: () => {
                setIsConfigSaving(false);
            }
        });
    };

    // Handle testing integration (specifically for Twilio SMS)
    const handleTestIntegration = async () => {
        if (!testPhoneNumber) {
            toast({
                title: "Error",
                description: "Please enter a phone number to test.",
                variant: "destructive",
            });
            return;
        }

        // Validate phone number format (E.164 format with country code)
        const phoneRegex = /^\+[1-9]\d{1,14}$/;
        if (!phoneRegex.test(testPhoneNumber)) {
            toast({
                title: "Invalid Phone Number",
                description: "Please enter a valid phone number with country code",
                variant: "destructive",
            });
            return;
        }

        if (!selectedIntegration?.id) {
            toast({
                title: "Error",
                description: "Integration ID is missing. Please try again.",
                variant: "destructive",
            });
            return;
        }

        setIsTestingIntegration(true);
        
        router.post(`/integrations/${selectedIntegration.id}/test`, {
            phoneNumber: testPhoneNumber,
            message: 'This is a test message from your DME system integration.'
        }, {
            onSuccess: () => {
                toast({
                    title: "Test Successful",
                    description: `Test SMS sent successfully to ${testPhoneNumber}`,
                });
                setTestPhoneNumber(""); // Clear the phone number after successful test
            },
            onError: (errors) => {
                console.error('Test error:', errors);
                toast({
                    title: "Test Failed",
                    description: errors.message || "Failed to send test SMS",
                    variant: "destructive",
                });
            },
            onFinish: () => {
                setIsTestingIntegration(false);
            }
        });
    };

    // Handle disconnect confirmation
    const handleDisconnectClick = (integration) => {
        setIntegrationToDisconnect(integration);
        setIsDisconnectModalOpen(true);
    };

    // Handle disconnect confirmation
    const handleDisconnectConfirm = async () => {
        if (!integrationToDisconnect?.id) {
            toast({
                title: "Error",
                description: "Integration ID is missing. Please try again.",
                variant: "destructive",
            });
            return;
        }
        
        setIsDisconnecting(true);
        
        router.put(`/integrations/${integrationToDisconnect.id}`, {
            config: [],  // Clear the configuration (empty array)
            is_active: false  // Mark as inactive
        }, {
            onSuccess: () => {
                // Show success toast first
                toast({
                    title: "Integration Disconnected",
                    description: `${integrationToDisconnect.name} has been disconnected and configuration cleared.`,
                });
                
                // Close modal and clear state after a brief delay to ensure toast is visible
                setTimeout(() => {
                    setIsDisconnectModalOpen(false);
                    setIntegrationToDisconnect(null);
                }, 500);
            },
            onError: (errors) => {
                console.error('Disconnect error:', errors);
                toast({
                    title: "Error",
                    description: errors.message || "Failed to disconnect integration",
                    variant: "destructive",
                });
            },
            onFinish: () => {
                setIsDisconnecting(false);
            }
        });
    };

    // Load Slack channels
    const loadSlackChannels = async (integration) => {
        if (!integration?.id || integration.name !== 'Slack') return;
        
        setIsLoadingChannels(true);
        try {
            const response = await fetch(`/integrations/${integration.id}/slack/channels`);
            const data = await response.json();
            
            if (data.success) {
                setSlackChannels(data.channels);
                setChannelsLoaded(true);
            } else {
                toast({
                    title: "Error",
                    description: `Failed to load channels: ${data.error}`,
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error('Error loading Slack channels:', error);
            toast({
                title: "Error",
                description: "Failed to load Slack channels",
                variant: "destructive",
            });
        } finally {
            setIsLoadingChannels(false);
        }
    };

    // Set default Slack channel
    const setSlackDefaultChannel = async (integration, channel) => {
        if (!integration?.id || integration.name !== 'Slack') return;
        
        try {
            const response = await fetch(`/integrations/${integration.id}/slack/default-channel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                body: JSON.stringify({
                    defaultChannel: channel
                })
            });

            const data = await response.json();
            
            if (data.success) {
                // Update the form data
                setConfigForm(prev => ({
                    ...prev,
                    defaultChannel: channel
                }));
                
                toast({
                    title: "Success",
                    description: "Default channel updated successfully",
                });
            } else {
                toast({
                    title: "Error",
                    description: `Failed to set default channel: ${data.error}`,
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error('Error setting default channel:', error);
            toast({
                title: "Error",
                description: "Failed to set default channel",
                variant: "destructive",
            });
        }
    };

    // Handle Slack test message
    const handleTestSlackMessage = async (integration) => {
        // Use testSlackChannel if set, otherwise fall back to default channel
        const channelToTest = testSlackChannel || configForm.defaultChannel;
        
        if (!channelToTest) {
            toast({
                title: "Error",
                description: "Please select a channel for testing.",
                variant: "destructive",
            });
            return;
        }

        setIsTestingIntegration(true);
        
        router.post(`/integrations/${integration.id}/test`, {
            channel: channelToTest,
            message: 'This is a test message from your DME system integration.'
        }, {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                toast({
                    title: "Test Successful",
                    description: `Test message sent successfully to #${channelToTest}`,
                });
                setTestSlackChannel(""); // Clear the channel after successful test
            },
            onError: (errors) => {
                console.error('Test error:', errors);
                let errorMessage = "Failed to send test message";
                
                if (errors.error) {
                    errorMessage = errors.error;
                    
                    // Handle specific Slack errors with helpful guidance
                    if (errorMessage.includes('not_in_channel')) {
                        errorMessage = `Bot is not a member of this channel. To fix this: 1) Go to the Slack channel, 2) Type /invite @your-bot-name, 3) Or manually add the bot from channel settings.`;
                    } else if (errorMessage.includes('channel_not_found')) {
                        errorMessage = `Channel not found. The channel may have been deleted or the bot doesn't have access to it.`;
                    } else if (errorMessage.includes('restricted_action')) {
                        errorMessage = `Bot doesn't have permission to post in this channel. Check your app's OAuth scopes include 'chat:write'.`;
                    }
                } else if (errors.message) {
                    errorMessage = errors.message;
                } else if (errors.errors) {
                    const validationErrors = Object.values(errors.errors).flat();
                    errorMessage = validationErrors.length > 0 ? validationErrors.join(', ') : errorMessage;
                }
                
                toast({
                    title: "Test Failed",
                    description: errorMessage,
                    variant: "destructive",
                });
            },
            onFinish: () => {
                setIsTestingIntegration(false);
            }
        });
    };

    // Get config fields for different integrations
    const getConfigFields = (integrationName) => {
        switch (integrationName) {
            case 'Twilio':
                return [
                    { 
                        key: 'accountSid', 
                        label: 'Account SID', 
                        type: 'text', 
                        required: true, 
                        placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
                        description: 'Your Twilio Account SID from the Console Dashboard. This uniquely identifies your Twilio account.',
                        helpUrl: 'https://console.twilio.com/'
                    },
                    { 
                        key: 'authToken', 
                        label: 'Auth Token', 
                        type: 'password', 
                        required: true, 
                        placeholder: 'Your auth token',
                        description: 'Your Twilio Auth Token from the Console Dashboard. Keep this secret and secure.',
                        helpUrl: 'https://console.twilio.com/'
                    },
                    { 
                        key: 'phoneNumber', 
                        label: 'Phone Number', 
                        type: 'phone', 
                        required: true, 
                        placeholder: 'Select country and enter phone number',
                        description: 'Your Twilio phone number for sending SMS messages. This must be a number you\'ve purchased from Twilio.',
                        helpUrl: 'https://console.twilio.com/us1/develop/phone-numbers/manage/incoming'
                    },
                ];
            case 'Slack':
                return [
                    { 
                        key: 'botToken', 
                        label: 'Bot Token (Workspace-wide)', 
                        type: 'password', 
                        required: false, 
                        placeholder: 'xoxb-1234567890-1234567890123-abcdefghijklmnopqrstuvwx',
                        description: 'Optional: Bot token for workspace-wide messaging. Get this from your Slack App settings under "OAuth & Permissions" → "Bot User OAuth Token"'
                    },
                    { 
                        key: 'clientId', 
                        label: 'Client ID (Individual User OAuth)', 
                        type: 'text', 
                        required: false, 
                        placeholder: '1234567890.1234567890',
                        description: 'Required for individual user connections. Get this from your Slack App settings under "Basic Information" → "App Credentials"'
                    },
                    { 
                        key: 'clientSecret', 
                        label: 'Client Secret (Individual User OAuth)', 
                        type: 'password', 
                        required: false, 
                        placeholder: 'abcdefghijklmnopqrstuvwxyz123456',
                        description: 'Required for individual user connections. Get this from your Slack App settings under "Basic Information" → "App Credentials"'
                    },
                    { 
                        key: 'redirectUri', 
                        label: 'Redirect URI (Auto-configured)', 
                        type: 'text', 
                        required: false, 
                        placeholder: window.location.origin + '/slack/callback',
                        description: 'This is automatically set. Add this URL to your Slack App\'s "OAuth & Permissions" → "Redirect URLs" section'
                    }
                ];
            default:
                return [];
        }
    };

    // Memoized onChange handlers to prevent focus loss
    const handleConfigFormChange = useCallback((fieldKey, value) => {
        setConfigForm(prev => ({
            ...prev,
            [fieldKey]: value || ""
        }));
    }, []);

    const handleTestPhoneNumberChange = useCallback((value) => {
        setTestPhoneNumber(value || "");
    }, []);

    // Create memoized onChange handlers for each field to prevent inline function recreation
    const getFieldChangeHandler = useCallback((fieldKey) => {
        return (value) => handleConfigFormChange(fieldKey, value);
    }, [handleConfigFormChange]);

    return (
        <>
            <Head title="DME | Integrations" />
            <AuthenticatedLayout>
            <div className="flex flex-col gap-6 w-full px-4 md:px-6 pb-8 pt-6">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            Integrations
                        </h1>
                        <p className="text-muted-foreground">
                            Connect your workflow system with other tools and
                            services
                        </p>
                    </div>
                    {permissions?.can_create_integrations && (
                        <Button
                            onClick={openCreateIntegrationModal}
                            className="dark:text-white shrink-0"
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Integration
                        </Button>
                    )}
                </div>

                <div className="flex flex-wrap gap-2 items-center justify-between">
                    <div className="flex flex-wrap gap-2 items-center">
                        {isMobile ? (
                            <FilterDropdown
                                onReset={resetFilters}
                                count={getActiveFilterCount()}
                            >
                                <div className="relative w-full">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="search"
                                        placeholder="Search integrations..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                    />
                                </div>

                                <Select
                                    value={categoryFilter}
                                    onValueChange={setCategoryFilter}
                                >
                                    <SelectTrigger className="w-full h-9">
                                        <SelectValue placeholder="Category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All Categories
                                        </SelectItem>
                                        <SelectItem value="crm">CRM</SelectItem>
                                        <SelectItem value="communication">
                                            Communication
                                        </SelectItem>
                                    </SelectContent>
                                </Select>

                                <Select
                                    value={statusFilter}
                                    onValueChange={setStatusFilter}
                                >
                                    <SelectTrigger className="w-full h-9">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All Statuses
                                        </SelectItem>
                                        <SelectItem value="connected">
                                            Connected
                                        </SelectItem>
                                        <SelectItem value="not-connected">
                                            Not Connected
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </FilterDropdown>
                        ) : (
                            <>
                                <div className="relative w-[280px]">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="search"
                                        placeholder="Search integrations..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                    />
                                </div>

                                <Select
                                    value={categoryFilter}
                                    onValueChange={setCategoryFilter}
                                >
                                    <SelectTrigger className="w-[180px] h-9">
                                        <SelectValue placeholder="Category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All Categories
                                        </SelectItem>
                                        <SelectItem value="crm">CRM</SelectItem>
                                        <SelectItem value="communication">
                                            Communication
                                        </SelectItem>
                                    </SelectContent>
                                </Select>

                                <Select
                                    value={statusFilter}
                                    onValueChange={setStatusFilter}
                                >
                                    <SelectTrigger className="w-[180px] h-9">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All Statuses
                                        </SelectItem>
                                        <SelectItem value="connected">
                                            Connected
                                        </SelectItem>
                                        <SelectItem value="not-connected">
                                            Not Connected
                                        </SelectItem>
                                    </SelectContent>
                                </Select>

                                {getActiveFilterCount() > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={resetFilters}
                                        className="h-9"
                                    >
                                        <X className="mr-2 h-4 w-4" />
                                        Reset
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="text-sm text-muted-foreground">
                    {filteredIntegrations.length} integration
                    {filteredIntegrations.length !== 1 ? "s" : ""} found
                </div>

                {filteredIntegrations.length === 0 ? (
                    <div className="flex h-40 items-center justify-center rounded-md border border-dashed">
                        <div className="flex flex-col items-center gap-2 text-center">
                            <Search className="h-8 w-8 text-muted-foreground" />
                            <h3 className="text-lg font-semibold">
                                No integrations found
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Try adjusting your search or filter to find what
                                you're looking for.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="integration-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                        {paginatedIntegrations.map((integration) => (
                            <Card 
                                key={integration.id} 
                                className={`group cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-2 ${
                                    integration.status === "connected" 
                                        ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20" 
                                        : integration.status === "mapping"
                                        ? "border-blue-200 bg-blue-50/40 dark:border-blue-800 dark:bg-blue-950/20"
                                        : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                                }`}
                                onClick={() => handleIntegrationClick(integration)}
                            >
                                <CardContent className="p-4 sm:p-6">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                        <div className="flex items-start gap-3 sm:gap-4 flex-1">
                                            <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${
                                                integration.status === "connected" 
                                                    ? "bg-green-100 dark:bg-green-900/30" 
                                                    : integration.status === "mapping"
                                                    ? "bg-blue-100 dark:bg-blue-900/30"
                                                    : "bg-gray-100 dark:bg-gray-800"
                                            }`}>
                                                {integration.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-gray-100 truncate">
                                                        {integration.name}
                                                    </h3>
                                                    {integration.status === "connected" && (
                                                        <div className="flex items-center gap-1">
                                                            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                                            <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                                                Connected
                                                            </span>
                                                        </div>
                                                    )}
                                                    {integration.status === "mapping" && (
                                                        <div className="flex items-center gap-1">
                                                            <Link className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                                Mapping only
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                                                    {integration.description}
                                                </p>
                                                {integration.category && (
                                                    <div className="mt-2">
                                                        <Badge 
                                                            variant="secondary" 
                                                            className="text-xs capitalize bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                                        >
                                                            {integration.category}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                                            {integration.status === "mapping" && permissions?.can_edit_integrations && (
                                                <Button 
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openMappingIntegrationModal(integration);
                                                    }}
                                                    className="whitespace-nowrap"
                                                >
                                                    <Settings className="w-4 h-4 mr-1" />
                                                    Manage
                                                </Button>
                                            )}
                                            {integration.status !== "connected" && integration.status !== "mapping" && (
                                                <Button 
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (integration.name === 'Twilio' || integration.name === 'Slack') {
                                                            openConfigModal(integration);
                                                        } else {
                                                            toast({
                                                                title: "Configuration Not Available",
                                                                description: `Configuration for ${integration.name} is not yet implemented.`,
                                                                variant: "destructive",
                                                            });
                                                        }
                                                    }}
                                                    className="whitespace-nowrap"
                                                >
                                                    <Link className="w-4 h-4 mr-1" />
                                                    Connect
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {filteredIntegrations.length > 0 && (
                    <div className="flex items-center justify-between p-4 mt-4 bg-card rounded-md border">
                        <div className="flex items-center space-x-2">
                            <Select
                                value={itemsPerPage.toString()}
                                onValueChange={(value) => {
                                    setItemsPerPage(Number(value));
                                    setPage(0);
                                }}
                            >
                                <SelectTrigger className="w-[100px] h-8">
                                    <SelectValue placeholder="Items" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="10">10 items</SelectItem>
                                    <SelectItem value="20">20 items</SelectItem>
                                    <SelectItem value="50">50 items</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="text-sm text-muted-foreground">
                                Showing{" "}
                                <strong>
                                    {Math.min(
                                        paginatedIntegrations.length,
                                        itemsPerPage
                                    )}
                                </strong>{" "}
                                of{" "}
                                <strong>{filteredIntegrations.length}</strong>{" "}
                                integrations
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 0}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            {Array.from({
                                length: Math.ceil(
                                    filteredIntegrations.length / itemsPerPage
                                ),
                            })
                                .map((_, index) => (
                                    <Button
                                        key={index}
                                        variant={
                                            page === index
                                                ? "default"
                                                : "outline"
                                        }
                                        size="sm"
                                        className="h-8 w-8"
                                        onClick={() => handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </Button>
                                ))
                                .slice(
                                    Math.max(0, page - 1),
                                    Math.min(
                                        page + 2,
                                        Math.ceil(
                                            filteredIntegrations.length /
                                                itemsPerPage
                                        )
                                    )
                                )}
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={
                                    page >=
                                    Math.ceil(
                                        filteredIntegrations.length /
                                            itemsPerPage
                                    ) -
                                        1
                                }
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Configuration Modal */}
                <Dialog 
                    open={isConfigModalOpen} 
                    onOpenChange={(open) => {
                        // Prevent closing modal during loading
                        if (!isConfigSaving) {
                            setIsConfigModalOpen(open);
                            if (!open) {
                                // Reset Slack-specific state when closing modal
                                setSlackChannels([]);
                                setChannelsLoaded(false);
                                setTestSlackChannel("");
                            }
                        }
                    }}
                >
                    <DialogContent className="sm:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader className="space-y-3 pb-6">
                            <DialogTitle className="flex items-center gap-3 text-xl">
                                <div className="p-3 rounded-full bg-primary/10">
                                    {selectedIntegration && getIntegrationIcon(selectedIntegration.name)}
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold">Configure {selectedIntegration?.name}</div>
                                    <div className="text-sm font-normal text-muted-foreground">
                                        {selectedIntegration?.is_active ? 'Update your configuration' : 'Set up your integration'}
                                    </div>
                                </div>
                            </DialogTitle>
                        </DialogHeader>
                        
                        <form onSubmit={handleConfigureIntegration} className="space-y-6">
                            {/* Configuration Section */}
                            <div className="space-y-6">
                                <div className="space-y-1">
                                    <h3 className="text-lg font-medium">Connection Settings</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Enter your {selectedIntegration?.name} API credentials to establish the connection.
                                    </p>
                                </div>
                                
                                {/* Twilio Configuration Info */}
                                {selectedIntegration?.name === 'Twilio' && (
                                    <div className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-950/20 dark:to-pink-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                                                <Phone className="h-4 w-4 text-red-600 dark:text-red-400" />
                                            </div>
                                            <div className="flex-1 space-y-2">
                                                <h4 className="font-medium text-red-900 dark:text-red-100">Twilio Setup Guide</h4>
                                                <div className="text-sm text-red-700 dark:text-red-300 space-y-2">
                                                    <p>To configure Twilio integration, you'll need:</p>
                                                    <ol className="list-decimal list-inside space-y-1 ml-2">
                                                        <li>A Twilio account (sign up at <a href="https://twilio.com" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">twilio.com</a>)</li>
                                                        <li>Your Account SID and Auth Token from the <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">Twilio Console</a></li>
                                                        <li>A purchased phone number for sending SMS messages</li>
                                                    </ol>
                                                    <p className="text-xs mt-2 opacity-90">
                                                        💡 <strong>Tip:</strong> You can get a free trial account with $15 credit to test the integration.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Slack Configuration Info */}
                                {selectedIntegration?.name === 'Slack' && (
                                    <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                                        <Info className="h-4 w-4" />
                                        <AlertDescription>
                                            <div className="space-y-3">
                                                <div className="font-medium text-blue-800 dark:text-blue-200">
                                                    Choose Your Slack Integration Type:
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    <div>
                                                        <strong>Workspace-wide (Bot Token):</strong> One shared bot for all users. Messages appear from the bot. Requires manual channel invitations.
                                                    </div>
                                                    <div>
                                                        <strong>Individual User OAuth:</strong> Each user connects their own account. Messages appear from the actual user. Access only their channels.
                                                    </div>
                                                    <div className="text-blue-700 dark:text-blue-300 italic">
                                                        You can configure both options - users will choose which to use.
                                                    </div>
                                                </div>
                                            </div>
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <div className="grid gap-5">
                                    {selectedIntegration && getConfigFields(selectedIntegration.name).map((field, index) => (
                                        <div key={field.key} className="space-y-3">
                                            <Label htmlFor={field.key} className="text-sm font-medium flex items-center gap-2">
                                                {field.label} 
                                                {field.required && <span className="text-red-500 text-xs">*</span>}
                                                {field.helpUrl && (
                                                    <a 
                                                        href={field.helpUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-blue-500 hover:text-blue-700 transition-colors"
                                                        title="Open Twilio Console"
                                                    >
                                                        <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                )}
                                            </Label>
                                            
                                            <div className="space-y-2">
                                                {field.type === 'phone' ? (
                                                    <PhoneInput
                                                        value={configForm[field.key] || ""}
                                                        onChange={getFieldChangeHandler(field.key)}
                                                        placeholder={field.placeholder}
                                                        required={field.required}
                                                        className="w-full"
                                                    />
                                                ) : (
                                                    <Input
                                                        id={field.key}
                                                        type={field.type}
                                                        placeholder={field.placeholder}
                                                        value={configForm[field.key] || ""}
                                                        onChange={(e) =>
                                                            handleConfigFormChange(field.key, e.target.value)
                                                        }
                                                        required={field.required}
                                                        className="w-full"
                                                    />
                                                )}
                                                
                                                {field.description && (
                                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                                        {field.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {selectedIntegration && getConfigFields(selectedIntegration.name).length === 0 && (
                                        <div className="text-center py-8">
                                            <p className="text-sm text-muted-foreground">
                                                No configuration options available for {selectedIntegration.name}.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Test Section - Only for Twilio and when active */}
                            {selectedIntegration?.name === 'Twilio' && selectedIntegration.is_active && (
                                <div className="border-t pt-6 space-y-6">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                                                <Send className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-medium">Test Your Integration</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Send a test SMS to verify everything is working correctly.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                                        <div className="space-y-3">
                                            <Label htmlFor="testPhoneNumber" className="text-sm font-medium">
                                                Recipient Phone Number <span className="text-red-500">*</span>
                                            </Label>
                                            <div className="flex gap-3">
                                                <PhoneInput
                                                    value={testPhoneNumber}
                                                    onChange={handleTestPhoneNumberChange}
                                                    placeholder="Select country and enter number"
                                                    className="flex-1"
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={handleTestIntegration}
                                                    disabled={isTestingIntegration || !testPhoneNumber}
                                                    className="whitespace-nowrap min-w-[100px]"
                                                    size="default"
                                                >
                                                    {isTestingIntegration ? (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                            Sending...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Send className="w-4 h-4 mr-2" />
                                                            Send Test
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                A test message will be sent from your configured Twilio number to verify the connection.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Slack Channel Management - Only when Slack is active */}
                            {selectedIntegration?.name === 'Slack' && selectedIntegration.is_active && (
                                <div className="border-t pt-6 space-y-6">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                                                <MessageSquare className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-medium">Slack Channels</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Manage channel access and set your default notification channel.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bot Info */}
                                    {selectedIntegration.config?.botInfo && (
                                        <div className="bg-muted/50 rounded-lg p-4">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <span className="text-sm font-medium">Connected as: {selectedIntegration.config.botInfo.user}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Team: {selectedIntegration.config.botInfo.team} • User ID: {selectedIntegration.config.botInfo.user_id}
                                            </div>
                                        </div>
                                    )}

                                    {/* Channel List */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium">Available Channels</Label>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => loadSlackChannels(selectedIntegration)}
                                                disabled={isLoadingChannels}
                                                className="h-8 px-3"
                                            >
                                                {isLoadingChannels ? (
                                                    <>
                                                        <div className="w-3 h-3 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                                                        Loading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <RotateCcw className="w-3 h-3 mr-2" />
                                                        Refresh
                                                    </>
                                                )}
                                            </Button>
                                        </div>

                                        {!channelsLoaded && !isLoadingChannels && (
                                            <div className="text-center py-6">
                                                <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                                <p className="text-sm text-muted-foreground">
                                                    Click "Refresh" to load your Slack channels
                                                </p>
                                            </div>
                                        )}

                                        {channelsLoaded && slackChannels.length > 0 && (
                                            <div className="space-y-4">
                                                {/* Default Channel Selection */}
                                                <div className="space-y-3">
                                                    <Label className="text-sm font-medium">Select Default Channel</Label>
                                                    <Select
                                                        value={configForm.defaultChannel || 'none'}
                                                        onValueChange={(value) => {
                                                            const channelValue = value === 'none' ? '' : value;
                                                            setConfigForm({ ...configForm, defaultChannel: channelValue });
                                                            if (channelValue) {
                                                                setSlackDefaultChannel(selectedIntegration, channelValue);
                                                            }
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue placeholder="Choose a default channel..." />
                                                        </SelectTrigger>
                                                        <SelectContent className="max-h-64">
                                                            <SelectItem value="none">No default channel</SelectItem>
                                                            {slackChannels.map((channel) => (
                                                                <SelectItem 
                                                                    key={channel.id} 
                                                                    value={channel.name}
                                                                    disabled={!channel.is_member}
                                                                    className={!channel.is_member ? 'opacity-50' : ''}
                                                                >
                                                                    <div className="flex items-center gap-2 w-full">
                                                                        <span className="text-muted-foreground text-sm">
                                                                            {channel.is_private ? '🔒' : '#'}
                                                                        </span>
                                                                        <span className="font-medium">{channel.name}</span>
                                                                        {channel.is_private && (
                                                                            <Badge variant="outline" className="text-xs h-4 px-1">Private</Badge>
                                                                        )}
                                                                        {!channel.is_member && (
                                                                            <Badge variant="destructive" className="text-xs h-4 px-1">Not Member</Badge>
                                                                        )}
                                                                        {channel.is_member && (
                                                                            <Badge variant="secondary" className="text-xs h-4 px-1 bg-green-100 text-green-800">Member</Badge>
                                                                        )}
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    
                                                    <div className="space-y-3">
                                                        <div className="flex items-center justify-between text-sm">
                                                            <span className="text-muted-foreground">
                                                                {slackChannels.length} channel{slackChannels.length !== 1 ? 's' : ''} found
                                                                {slackChannels.filter(c => c.is_member).length > 0 && (
                                                                    <span className="text-green-600 font-medium ml-2">
                                                                        • {slackChannels.filter(c => c.is_member).length} accessible
                                                                    </span>
                                                                )}
                                                                {configForm.defaultChannel && (
                                                                    <span className="text-primary font-medium ml-2">
                                                                        • Default: #{configForm.defaultChannel}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => loadSlackChannels(selectedIntegration)}
                                                                disabled={isLoadingChannels}
                                                                className="h-7 px-2 gap-1"
                                                            >
                                                                {isLoadingChannels ? (
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                                                                ) : (
                                                                    <RefreshCw className="h-3 w-3" />
                                                                )}
                                                                <span className="hidden sm:inline">Refresh</span>
                                                            </Button>
                                                        </div>

                                                        {slackChannels.length > 0 && slackChannels.filter(c => c.is_member).length === 0 && (
                                                            <Alert className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
                                                                <Info className="h-4 w-4 text-amber-600" />
                                                                <AlertDescription className="text-sm">
                                                                    <div className="space-y-2">
                                                                        <p className="font-medium text-amber-800 dark:text-amber-200">
                                                                            Bot needs to be added to channels
                                                                        </p>
                                                                        <div className="text-amber-700 dark:text-amber-300 space-y-1">
                                                                            <p><strong>To add your bot to a channel:</strong></p>
                                                                            <ol className="list-decimal list-inside space-y-0.5 text-xs">
                                                                                <li>Go to the Slack channel</li>
                                                                                <li>Type: <code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded">/invite @your-bot-name</code></li>
                                                                                <li>Or click the channel name → Settings → Integrations → Add apps</li>
                                                                            </ol>
                                                                        </div>
                                                                    </div>
                                                                </AlertDescription>
                                                            </Alert>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Channel List (Collapsible) */}
                                                <details className="group">
                                                    <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                                                        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                                                        View All Channels ({slackChannels.filter(c => c.is_member).length}/{slackChannels.length} accessible)
                                                    </summary>
                                                    <div className="mt-3 max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                                                        {slackChannels.map((channel) => (
                                                            <div
                                                                key={channel.id}
                                                                className={`flex items-center justify-between p-2 rounded-md text-sm transition-all ${
                                                                    configForm.defaultChannel === channel.name 
                                                                        ? 'bg-primary/10 border border-primary/20' 
                                                                        : !channel.is_member 
                                                                        ? 'bg-red-50 dark:bg-red-950/20 opacity-75'
                                                                        : 'hover:bg-muted/50'
                                                                }`}
                                                            >
                                                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                                                    <span className="text-muted-foreground">
                                                                        {channel.is_private ? '🔒' : '#'}
                                                                    </span>
                                                                    <span className={`font-medium ${!channel.is_member ? 'text-muted-foreground' : ''}`}>
                                                                        {channel.name}
                                                                    </span>
                                                                    {channel.is_private && (
                                                                        <Badge variant="outline" className="text-xs h-4 px-1">Private</Badge>
                                                                    )}
                                                                    {!channel.is_member && (
                                                                        <Badge variant="destructive" className="text-xs h-4 px-1">Not Member</Badge>
                                                                    )}
                                                                    {channel.is_member && (
                                                                        <Badge variant="secondary" className="text-xs h-4 px-1 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">Member</Badge>
                                                                    )}
                                                                    {configForm.defaultChannel === channel.name && (
                                                                        <Badge variant="default" className="text-xs h-4 px-1">Default</Badge>
                                                                    )}
                                                                </div>
                                                                {(channel.purpose || channel.topic) && (
                                                                    <span className="text-xs text-muted-foreground max-w-32 truncate ml-2">
                                                                        {channel.purpose || channel.topic}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </details>
                                                
                                                {/* Test Message Section */}
                                                <div className="border-t pt-4 space-y-3">
                                                    <Label className="text-sm font-medium">Test Message</Label>
                                                    <div className="flex gap-2">
                                                        <Select 
                                                            value={testSlackChannel || configForm.defaultChannel || ""} 
                                                            onValueChange={setTestSlackChannel}
                                                        >
                                                            <SelectTrigger className="flex-1">
                                                                <SelectValue placeholder="Select channel for test" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {slackChannels
                                                                    .filter(channel => channel.is_member)
                                                                    .map((channel) => (
                                                                        <SelectItem key={channel.id} value={channel.name}>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-muted-foreground text-sm">
                                                                                    {channel.is_private ? '🔒' : '#'}
                                                                                </span>
                                                                                <span>{channel.name}</span>
                                                                                {channel.is_private && (
                                                                                    <Badge variant="outline" className="text-xs h-4 px-1">Private</Badge>
                                                                                )}
                                                                            </div>
                                                                        </SelectItem>
                                                                    ))}
                                                                {slackChannels.filter(channel => channel.is_member).length === 0 && (
                                                                    <div className="p-2 text-sm text-muted-foreground text-center">
                                                                        No channels available. Bot needs to be added to channels first.
                                                                    </div>
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                        <Button
                                                            type="button"
                                                            onClick={() => handleTestSlackMessage(selectedIntegration)}
                                                            disabled={isTestingIntegration || (!testSlackChannel && !configForm.defaultChannel)}
                                                            className="whitespace-nowrap"
                                                            size="default"
                                                        >
                                                            {isTestingIntegration ? (
                                                                <>
                                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                                    Sending...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Send className="w-4 h-4 mr-2" />
                                                                    Send Test
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                    <div className="space-y-1">
                                                        <p className="text-xs text-muted-foreground">
                                                            Send a test message to verify the Slack integration is working.
                                                        </p>
                                                        {(testSlackChannel || configForm.defaultChannel) && (
                                                            <p className="text-xs text-blue-600 dark:text-blue-400">
                                                                Will send to: #{testSlackChannel || configForm.defaultChannel}
                                                                {!testSlackChannel && configForm.defaultChannel && " (default channel)"}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {channelsLoaded && slackChannels.length === 0 && (
                                            <div className="text-center py-6">
                                                <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                                <p className="text-sm text-muted-foreground">
                                                    No channels found. Make sure your bot has access to channels.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <DialogFooter className="flex gap-3 pt-6 border-t">
                                {/* Disconnect button for connected integrations */}
                                {selectedIntegration?.is_active && (
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        onClick={() => {
                                            setIntegrationToDisconnect(selectedIntegration);
                                            setIsDisconnectModalOpen(true);
                                        }}
                                        className="mr-auto"
                                    >
                                        <X className="w-4 h-4 mr-2" />
                                        Disconnect Integration
                                    </Button>
                                )}
                                
                                <div className="flex gap-3 ml-auto">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setIsConfigModalOpen(false)}
                                        disabled={isConfigSaving}
                                        className="min-w-[80px]"
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        disabled={isConfigSaving || (selectedIntegration && getConfigFields(selectedIntegration.name).length === 0)}
                                        className="min-w-[140px]"
                                    >
                                        {isConfigSaving ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Settings className="w-4 h-4 mr-2" />
                                                Save Configuration
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Disconnect Confirmation Modal */}
                <AlertDialog 
                    open={isDisconnectModalOpen} 
                    onOpenChange={(open) => {
                        // Prevent closing modal during loading
                        if (!isDisconnecting) {
                            setIsDisconnectModalOpen(open);
                        }
                    }}
                >
                    <AlertDialogContent className="sm:max-w-[500px]">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-3 text-xl">
                                <div className="p-3 rounded-full bg-destructive/10">
                                    {integrationToDisconnect && getIntegrationIcon(integrationToDisconnect.name)}
                                </div>
                                <div>
                                    <div className="font-semibold">Disconnect {integrationToDisconnect?.name}</div>
                                    <div className="text-sm font-normal text-muted-foreground">
                                        This will remove your integration connection
                                    </div>
                                </div>
                            </AlertDialogTitle>
                            <AlertDialogDescription asChild>
                                <div className="space-y-4 pt-2">
                                    <p className="text-sm">
                                        Are you sure you want to disconnect from {integrationToDisconnect?.name}? 
                                        This action cannot be undone.
                                    </p>
                                    
                                    <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="p-1 rounded-full bg-destructive/10 mt-0.5">
                                                <X className="h-3 w-3 text-destructive" />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-destructive">
                                                    This action will:
                                                </div>
                                                <ul className="text-sm text-muted-foreground space-y-1">
                                                    <li>• Clear all saved configuration settings</li>
                                                    <li>• Stop all active integrations and workflows</li>
                                                    <li>• Disable any automated processes using this integration</li>
                                                    <li>• Require complete reconfiguration to reconnect</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {integrationToDisconnect?.name === 'Slack' && (
                                        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                                <strong>Note:</strong> Your personal Slack connection (if any) will remain active. 
                                                This only affects the workspace-wide bot integration.
                                            </p>
                                        </div>
                                    )}
                                    
                                    {integrationToDisconnect?.name === 'Twilio' && (
                                        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                            <p className="text-xs text-amber-700 dark:text-amber-300">
                                                <strong>Note:</strong> All SMS sending capabilities will be disabled until you reconnect.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="gap-3">
                            <AlertDialogCancel disabled={isDisconnecting} className="min-w-[80px]">
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDisconnectConfirm}
                                disabled={isDisconnecting}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 min-w-[120px]"
                            >
                                {isDisconnecting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Disconnecting...
                                    </>
                                ) : (
                                    <>
                                        <X className="w-4 h-4 mr-2" />
                                        Yes, Disconnect
                                    </>
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <IntegrationFormModal
                    open={isIntegrationFormOpen}
                    onOpenChange={setIsIntegrationFormOpen}
                    mode={integrationFormMode}
                    integration={selectedMappingIntegration}
                    canEdit={
                        integrationFormMode === "create"
                            ? permissions?.can_create_integrations
                            : permissions?.can_edit_integrations
                    }
                    canDelete={
                        integrationFormMode === "edit" &&
                        permissions?.can_delete_integrations
                    }
                    onSuccess={reloadIntegrations}
                />
            </div>
        </AuthenticatedLayout>
        </>
    );
}
